import {
  PlayArrow as PlayIcon,
  Speed as SpeedIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

type LogEntry = {
  id: number;
  message: string;
  type: "success" | "ratelimit" | "error" | "info";
};

type ApiTarget = "production" | "local" | "custom";

const API_URLS: Record<Exclude<ApiTarget, "custom">, string> = {
  production: "https://api.pragmaticemailvalidator.com",
  local: "http://localhost:5024",
};

export function HammerPage() {
  const [apiTarget, setApiTarget] = useState<ApiTarget>("production");
  const [customUrl, setCustomUrl] = useState("");
  const [email, setEmail] = useState("münchen.café@рф.中国");
  const [requestCount, setRequestCount] = useState(110);
  const [concurrency, setConcurrency] = useState(1);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    rateLimit: 0,
    errors: 0,
  });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [detectedIp, setDetectedIp] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const logIdRef = useRef(0);

  const getApiUrl = useCallback(() => {
    if (apiTarget === "custom") return customUrl;
    return API_URLS[apiTarget];
  }, [apiTarget, customUrl]);

  const addLog = useCallback((message: string, type: LogEntry["type"]) => {
    const id = ++logIdRef.current;
    setLogs((prev) => [...prev, { id, message, type }]);
  }, []);

  const extractIpFromDetail = (detail: string): string | null => {
    const match = detail.match(/IP address ([^\s.]+(?:\.[^\s.]+)*|\S+)/);
    return match ? match[1] : null;
  };

  const startCountdown = useCallback((seconds: number) => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setCountdown(seconds);
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const runTest = useCallback(async () => {
    const apiUrl = getApiUrl();
    if (!apiUrl) {
      addLog("Please enter an API URL", "error");
      return;
    }

    // Reset state
    setLogs([]);
    setProgress(0);
    setStats({ total: 0, success: 0, rateLimit: 0, errors: 0 });
    setDetectedIp(null);
    setCountdown(null);
    setElapsed(null);
    setIsRunning(true);
    logIdRef.current = 0;

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    abortControllerRef.current = new AbortController();

    const startTime = performance.now();

    addLog(`Target: ${apiUrl}`, "info");
    addLog(`Email: ${email}`, "info");
    addLog(`Count: ${requestCount}`, "info");
    addLog(`Concurrency: ${concurrency}`, "info");

    const completedRef = { current: 0 };
    const statsRef = { success: 0, rateLimit: 0, errors: 0 };
    const nextIndexRef = { current: 0 };

    const makeRequest = async (): Promise<void> => {
      while (true) {
        const i = ++nextIndexRef.current;
        if (i > requestCount) return;
        if (abortControllerRef.current?.signal.aborted) return;

        try {
          const response = await fetch(`${apiUrl}/api/v1/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
            signal: abortControllerRef.current?.signal,
          });

          const data = await response.json().catch(() => ({}));

          if (response.ok) {
            statsRef.success++;
            addLog(`[${i}/${requestCount}] ✅ 200 OK`, "success");
          } else if (response.status === 429) {
            statsRef.rateLimit++;
            const detail = data.detail || "Rate limited";
            addLog(`[${i}/${requestCount}] 🚫 429 - ${detail}`, "ratelimit");

            const ip = extractIpFromDetail(detail);
            if (ip) setDetectedIp(ip);

            const retryAfter = response.headers.get("Retry-After");
            if (retryAfter) {
              startCountdown(parseInt(retryAfter, 10));
            } else {
              const match = detail.match(/(\d+)\s*seconds/);
              if (match) startCountdown(parseInt(match[1], 10));
            }
          } else {
            statsRef.errors++;
            addLog(
              `[${i}/${requestCount}] ❌ ${response.status} - ${data.title || "Error"}`,
              "error",
            );
          }
        } catch (err) {
          if ((err as Error).name === "AbortError") {
            addLog("Test stopped by user", "info");
            return;
          }
          statsRef.errors++;
          addLog(
            `[${i}/${requestCount}] ❌ Network error: ${(err as Error).message}`,
            "error",
          );
        }

        completedRef.current++;
        setStats({
          total: completedRef.current,
          success: statsRef.success,
          rateLimit: statsRef.rateLimit,
          errors: statsRef.errors,
        });
        setProgress((completedRef.current / requestCount) * 100);
      }
    };

    // Launch concurrent workers
    const workers = Array.from(
      { length: Math.min(concurrency, requestCount) },
      () => makeRequest(),
    );
    await Promise.all(workers);

    const elapsedMs = performance.now() - startTime;
    const elapsedSec = (elapsedMs / 1000).toFixed(2);
    const rps = (
      completedRef.current > 0 ? completedRef.current / (elapsedMs / 1000) : 0
    ).toFixed(1);
    setElapsed(`${elapsedSec}s (${rps} req/s)`);
    addLog(`Completed in ${elapsedSec}s (${rps} req/s)`, "info");

    setIsRunning(false);
    abortControllerRef.current = null;
  }, [getApiUrl, email, requestCount, concurrency, addLog, startCountdown]);

  const stopTest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  const getLogColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "success":
        return "success.main";
      case "ratelimit":
        return "warning.main";
      case "error":
        return "error.main";
      default:
        return "text.secondary";
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        Rate Limit Tester
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Test API rate limiting behavior from your current network. Useful for
        testing from different locations like Tor or VPN.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>API Target</InputLabel>
              <Select
                value={apiTarget}
                label="API Target"
                onChange={(e) => setApiTarget(e.target.value as ApiTarget)}
                disabled={isRunning}
              >
                <MenuItem value="production">
                  Production (api.pragmaticemailvalidator.com)
                </MenuItem>
                <MenuItem value="local">Local (localhost:5024)</MenuItem>
                <MenuItem value="custom">Custom URL...</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {apiTarget === "custom" && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Custom API URL"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://your-api.example.com"
                disabled={isRunning}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Test Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isRunning}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Request Count"
              type="number"
              value={requestCount}
              onChange={(e) =>
                setRequestCount(
                  Math.max(
                    1,
                    Math.min(5000, parseInt(e.target.value, 10) || 1),
                  ),
                )
              }
              slotProps={{ htmlInput: { min: 1, max: 5000 } }}
              disabled={isRunning}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Concurrency"
              type="number"
              value={concurrency}
              onChange={(e) =>
                setConcurrency(
                  Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)),
                )
              }
              slotProps={{ htmlInput: { min: 1, max: 50 } }}
              disabled={isRunning}
              helperText="Parallel requests (1-50)"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayIcon />}
                onClick={runTest}
                disabled={isRunning}
                sx={{ flex: 1 }}
              >
                Start Test
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<StopIcon />}
                onClick={stopTest}
                disabled={!isRunning}
              >
                Stop
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {(stats.total > 0 || isRunning) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" component="div">
                  {stats.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  REQUESTS
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" component="div" color="success.main">
                  {stats.success}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  SUCCESS
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" component="div" color="warning.main">
                  {stats.rateLimit}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  RATE LIMITED
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" component="div" color="error.main">
                  {stats.errors}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ERRORS
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {(detectedIp || countdown !== null || elapsed) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                {detectedIp && (
                  <Chip
                    label={`Your IP: ${detectedIp}`}
                    color="info"
                    variant="outlined"
                  />
                )}
                {countdown !== null && (
                  <Chip
                    icon={<SpeedIcon />}
                    label={`Retry in ${countdown}s`}
                    color="warning"
                  />
                )}
                {elapsed && (
                  <Chip label={elapsed} color="default" variant="outlined" />
                )}
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Request Log
          </Typography>
          <Box
            sx={{
              maxHeight: 300,
              overflow: "auto",
              bgcolor: "grey.50",
              borderRadius: 1,
              p: 1,
              fontFamily: "monospace",
              fontSize: "0.8rem",
            }}
          >
            <List dense disablePadding>
              {logs.map((log) => (
                <ListItem key={log.id} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={log.message}
                    primaryTypographyProps={{
                      sx: {
                        color: getLogColor(log.type),
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>How it works</AlertTitle>
        This tool sends rapid API requests to trigger rate limiting. Default
        rate limit: <strong>100 requests</strong> per{" "}
        <strong>60-second window</strong> per IP address (configurable via{" "}
        <code>RateLimiting:PermitLimit</code> and{" "}
        <code>RateLimiting:WindowSeconds</code> in appsettings). When rate
        limited, you'll see your IP address as detected by the API and a
        countdown until the limit resets.
      </Alert>
    </Box>
  );
}
